import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Avatar } from '../ui';

export interface MentionUser {
  id: string;
  name: string;
  email: string;
  profileImage?: string | null;
}

export interface MentionListRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

interface MentionListProps {
  items: MentionUser[];
  command: (item: { id: string; label: string }) => void;
}

export const MentionList = forwardRef<MentionListRef, MentionListProps>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => setSelectedIndex(0), [props.items]);

  const selectItem = (index: number) => {
    const item = props.items[index];
    if (item) props.command({ id: item.id, label: item.name });
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === 'ArrowUp') {
        setSelectedIndex((i) => (i + props.items.length - 1) % props.items.length);
        return true;
      }
      if (event.key === 'ArrowDown') {
        setSelectedIndex((i) => (i + 1) % props.items.length);
        return true;
      }
      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }
      return false;
    },
  }));

  if (props.items.length === 0) {
    return (
      <div className="tt-mention-list">
        <div className="tt-mention-empty">לא נמצאו משתמשים</div>
      </div>
    );
  }

  return (
    <div className="tt-mention-list">
      {props.items.map((user, index) => (
        <button
          key={user.id}
          type="button"
          className={[
            'tt-mention-item',
            selectedIndex === index ? 'tt-mention-item-active' : '',
          ].join(' ')}
          onClick={() => selectItem(index)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          {user.profileImage ? (
            <img src={user.profileImage} alt={user.name} className="size-7 rounded-full object-cover" />
          ) : (
            <Avatar name={user.name} size="sm" />
          )}
          <div className="tt-mention-item-info">
            <div className="tt-mention-item-name">{user.name}</div>
            <div className="tt-mention-item-email">{user.email}</div>
          </div>
        </button>
      ))}
    </div>
  );
});

MentionList.displayName = 'MentionList';
